import { useState } from "react";
import { toast } from "sonner";
import { Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { adminApi } from "@/lib/auth";
import { errorText } from "@/lib/errors";
import { CATEGORY_LABELS } from "@/pages/Resources";
import { Empty, Loading, useAdminList } from "./shared";

const input = "mt-1 block w-full bg-transparent border-b border-pine/40 py-2 text-base focus:outline-none focus:border-brass transition-colors";
const BLANK = { title: "", category: "deployment", summary: "", body: "", link: "", status: "draft" };

const GuideForm = ({ open, onOpenChange, guide, onSaved }) => {
  const [form, setForm] = useState(BLANK);
  const [initFor, setInitFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const key = guide?.id || "new";
  if (open && initFor !== key) { setInitFor(key); setForm(guide ? { ...guide, link: guide.link || "" } : BLANK); }
  if (!open && initFor) setInitFor(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { title: form.title, category: form.category, summary: form.summary, body: form.body, link: form.link || null, status: form.status };
    try {
      if (guide) await adminApi.put(`/admin/resources/${guide.id}`, body);
      else await adminApi.post("/admin/resources", body);
      toast.success(form.status === "published" ? "Guide published on your website." : "Draft saved.");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="site bg-paper text-pine border-none rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="guide-form-dialog">
        <DialogHeader>
          <DialogTitle className="dp text-3xl font-normal">{guide ? "Edit guide" : "Add a guide"}</DialogTitle>
          <DialogDescription className="text-moss">Start a line on its own, followed by text, to make it a heading. Leave a blank line between sections.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4" data-testid="guide-form">
          <label className="text-sm">Title<input required value={form.title} onChange={set("title")} className={input} data-testid="guide-title-input" /></label>
          <div className="text-sm">
            Topic
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1 bg-transparent border-pine/40" data-testid="guide-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABELS).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`guide-category-${v}`}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="text-sm">Short summary<input required value={form.summary} onChange={set("summary")} className={input} data-testid="guide-summary-input" /></label>
          <label className="text-sm">Guide text<textarea required rows="12" value={form.body} onChange={set("body")} className={input} data-testid="guide-body-input" /></label>
          <label className="text-sm">Helpful link (optional, starts with https://)<input value={form.link} onChange={set("link")} className={input} data-testid="guide-link-input" /></label>
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={form.status === "published"} onCheckedChange={(v) => setForm({ ...form, status: v ? "published" : "draft" })} data-testid="guide-published-switch" />
            Publish on the website
          </label>
          <button type="submit" disabled={saving} className="mt-2 rounded-full bg-pine text-sage py-4 disabled:opacity-70" data-testid="guide-save-button">{saving ? "Saving..." : "Save guide"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const GuidesTab = () => {
  const { items, reload } = useAdminList("/admin/resources");
  const { items: news } = useAdminList("/admin/newsletter");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const openForm = (g) => { setEditing(g); setOpen(true); };
  const remove = async (g) => {
    if (!window.confirm(`Delete "${g.title}"?`)) return;
    try { await adminApi.delete(`/admin/resources/${g.id}`); toast.success("Guide deleted."); reload(); } catch (err) { toast.error(errorText(err)); }
  };

  return (
    <div>
      <p className="mb-6 text-moss max-w-2xl">Starter drafts were written for you to review. Nothing appears on the website until you switch on "Publish".</p>
      {news && (
        <p className="mb-6 rounded-2xl bg-paper px-6 py-4 max-w-2xl" data-testid="newsletter-subscriber-count">
          <span className="dp text-3xl tn">{news.counts.confirmed}</span> {news.counts.confirmed === 1 ? "person gets" : "people get"} an email the first time you publish each guide.
        </p>
      )}
      <button type="button" onClick={() => openForm(null)} className="mb-6 inline-flex items-center gap-2 rounded-full bg-pine text-sage px-6 py-3" data-testid="guide-add-button"><Plus size={16} /> Add a guide</button>
      {!items && <Loading />}
      {items && !items.length && <Empty testId="admin-guides-empty">No guides yet.</Empty>}
      <ul className="grid gap-4" data-testid="admin-guides-list">
        {items?.map((g) => (
          <li key={g.id} className="bg-paper rounded-2xl p-6 grid sm:grid-cols-[1fr_auto] gap-4" data-testid={`admin-guide-${g.id}`}>
            <div className="min-w-0">
              <p className="lb text-brass-ink">{g.status === "published" ? "Published" : "Draft"} · {CATEGORY_LABELS[g.category]}</p>
              <p className="dp text-2xl mt-2">{g.title}</p>
              <p className="text-sm text-moss mt-1">{g.summary}</p>
            </div>
            <div className="flex sm:flex-col gap-4 items-start">
              <button type="button" onClick={() => openForm(g)} className="inline-flex items-center gap-1 text-sm hover:text-brass-ink" data-testid={`guide-edit-${g.id}`}><Pencil size={14} /> Review & edit</button>
              {g.status === "published" && <a href={`/resources/${g.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm" data-testid={`guide-view-${g.id}`}><Eye size={14} /> View</a>}
              <button type="button" onClick={() => remove(g)} className="inline-flex items-center gap-1 text-sm text-moss hover:text-red-800" data-testid={`guide-delete-${g.id}`}><Trash2 size={14} /> Delete</button>
            </div>
          </li>
        ))}
      </ul>
      <GuideForm open={open} onOpenChange={setOpen} guide={editing} onSaved={reload} />
    </div>
  );
};
